import React from "react";
import { ArrowUpRight } from "lucide-react";
import { useEntitlements } from "@/lib/useEntitlements";
import StalenessBanner from "@/components/landing/StalenessBanner";
import { appUrl } from "@/lib/referral";

const APP_SIGNUP = "https://work.voxiumscript.com";

const formatUSD = (cents) => {
  if (cents === 0 || cents === "0") return "$0";
  const n = Number(cents);
  if (!Number.isFinite(n)) return "—";
  // Whole dollars only; add thousands separator for annual.
  return n >= 1000
    ? `$${n.toLocaleString("en-US")}`
    : `$${n}`;
};

const computeAnnualSavings = (monthly, annual) => {
  const m = Number(monthly);
  const a = Number(annual);
  if (!Number.isFinite(m) || !Number.isFinite(a)) return null;
  const save = m * 12 - a;
  if (save <= 0) return null;
  return `or ${formatUSD(a)}/year — save ${formatUSD(save)}`;
};

// Tier copy is authored here (voice, order, "best for"), numbers come from
// the entitlements hook. If numbers are absent, we render qualitative
// wording only — never a hardcoded cap masquerading as current.
const TIER_BLUEPRINT = [
  {
    slug: "free",
    name: "Free",
    body:
      "One free Council read, plus Foundation and a short-form reads allotment. Lifetime — no credit card required.",
    testId: "tier-free",
  },
  {
    slug: "standard",
    name: "Standard",
    body:
      "A monthly allotment of Council reads, all short-form reads, and generous monthly Foundation reads and audio playback. For writers in active development.",
    testId: "tier-standard",
  },
  {
    slug: "pro",
    name: "Pro",
    body:
      "Everything in Standard, plus Pitch Meeting Simulation and higher monthly limits across the board. For writers preparing for the room.",
    testId: "tier-pro",
  },
  {
    slug: "pro_plus",
    name: "Pro Plus",
    body:
      "Everything in Pro, with the highest monthly limits — the fullest allotment of Council sessions, Foundation reads, Q&A follow-ups, and audio playback. For writers running multiple projects in parallel.",
    testId: "tier-pro-plus",
  },
];

const QA_PACKS_BLUEPRINT = [
  {
    slug: "qa_first_taste",
    name: "First Taste",
    body: "Three Q&A follow-ups. Available once per account.",
    testId: "qa-pack-first-taste",
  },
  {
    slug: "qa_sip",
    name: "Sip",
    body: "Five Q&A follow-ups.",
    testId: "qa-pack-sip",
  },
  {
    slug: "qa_deep_dive",
    name: "Deep Dive",
    body: "Twenty Q&A follow-ups.",
    testId: "qa-pack-deep-dive",
  },
];

export const Pricing = () => {
  const { data, isStale, lastUpdated, error } = useEntitlements();

  const tiers = TIER_BLUEPRINT.map((t) => {
    const ent = data?.tiers?.[t.slug] || {};
    const price = ent.price != null ? formatUSD(ent.price) : "—";
    const cadence =
      ent.cadence === "lifetime"
        ? "lifetime"
        : ent.cadence === "one-time"
        ? "one-time"
        : ent.cadence === "month" || ent.cadence == null
        ? "per month"
        : ent.cadence;
    const annual =
      ent.annualPrice != null && ent.price != null
        ? computeAnnualSavings(ent.price, ent.annualPrice)
        : null;
    return { ...t, price, cadence, annual };
  });

  const packs = QA_PACKS_BLUEPRINT.map((p) => {
    const ent = data?.packs?.[p.slug] || {};
    const price = ent.price != null ? formatUSD(ent.price) : "—";
    return { ...p, price };
  });

  return (
    <section
      id="pricing"
      className="relative bg-cream w-full px-6 sm:px-10 lg:px-16 pt-28 sm:pt-32 pb-28 sm:pb-32 border-t border-cream-200"
      data-testid="pricing-section"
    >
      <div className="max-w-[78rem] mx-auto">
        {/* Section eyebrow + headline */}
        <div className="max-w-prose3 mb-16 sm:mb-20">
          <p className="eyebrow text-gold-900 mb-5" data-testid="pricing-eyebrow">
            Pricing
          </p>
          <h2 className="display-serif text-nearblack text-4xl sm:text-5xl leading-[1.05] mb-4">
            Affordable for serious writers, at every level.
          </h2>
          <p className="font-body text-sm text-nearblack/70" data-testid="pricing-currency-note">
            All prices are in US dollars (USD).
          </p>
        </div>

        {/* Staleness banner — appears only if the live entitlements fetch failed. */}
        {isStale && (
          <StalenessBanner lastUpdated={lastUpdated} error={error} />
        )}

        {/* Tiers — editorial paragraph treatment, NOT feature tables */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-14 mb-16 sm:mb-20">
          {tiers.map((tier) => (
            <div
              key={tier.testId}
              className="border-t border-cream-200 pt-8"
              data-testid={tier.testId}
            >
              <div className="flex items-baseline justify-between gap-4 mb-4">
                <h3 className="display-serif text-nearblack text-2xl">
                  {tier.name}
                </h3>
                <div className="text-right">
                  <span className="display-serif text-nearblack text-2xl">
                    {tier.price}
                  </span>
                  <span className="font-ui text-xs text-nearblack/70 block mt-0.5">
                    {tier.cadence}
                  </span>
                </div>
              </div>
              {tier.annual && (
                <p
                  className="font-body italic text-sm text-gold-900 mb-3"
                  data-testid={`${tier.testId}-annual`}
                >
                  {tier.annual}
                </p>
              )}
              <p className="font-body text-base leading-relaxed text-nearblack/75 max-w-[42ch]">
                {tier.body}
              </p>
            </div>
          ))}
        </div>

        {/* Q&A Follow-Ups — packs of follow-up questions usable on a Council
            read you already own. NOT one-off Mentor conversations; the app
            distinguishes "Q&A follow-ups" (questions to a Council reader)
            from "Mentor exchanges" (turns in Development Mode). Copy here
            matches the "Q&A follow-ups" product exactly. */}
        <div className="mb-16 sm:mb-20" data-testid="qa-followups-section">
          <div className="max-w-prose3 mb-10 sm:mb-12">
            <p className="eyebrow text-gold-900 mb-5" data-testid="qa-followups-eyebrow">
              Q&amp;A Follow-Ups
            </p>
            <p className="font-body text-base leading-relaxed text-nearblack/75 max-w-[52ch]">
              Packs of follow-up questions you can put to a Council reader on a
              coverage you already own. Buy once, use anytime.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-12 gap-y-10">
            {packs.map((pack) => (
              <div
                key={pack.testId}
                className="border-t border-cream-200 pt-8"
                data-testid={pack.testId}
              >
                <div className="flex items-baseline justify-between gap-4 mb-3">
                  <h3 className="display-serif text-nearblack text-xl">
                    {pack.name}
                  </h3>
                  <span className="display-serif text-nearblack text-xl">
                    {pack.price}
                  </span>
                </div>
                <p className="font-body text-base leading-relaxed text-nearblack/75">
                  {pack.body}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Footnote + single CTA. Overage claim narrowed: no "all subscription
            tiers" until the entitlements endpoint publishes which meters
            carry overage. */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-8 border-t border-cream-200 pt-10">
          <p className="font-body italic text-sm text-nearblack/70 max-w-[44ch]" data-testid="pricing-footnote">
            Overage packs are available on select meters. Cancel anytime — your
            work, your call.
          </p>
          <a
            href={appUrl(APP_SIGNUP)}
            className="btn-gold self-start sm:self-auto"
            data-testid="pricing-cta"
          >
            Start free
            <ArrowUpRight size={16} strokeWidth={2} />
          </a>
        </div>
      </div>
    </section>
  );
};

export default Pricing;
